/**
 * 操作日志：所有设备密钥/仪器激活码/维护动态密码操作的唯一可追溯入口。
 *
 * 集中式密钥模型下，管理端可以算出任意设备的动态码，因此「留痕 + 可检索」
 * 是这套设计的主要补偿控制。
 */
import { useState } from 'react'
import { Download, Loader2, RefreshCw, Search, TriangleAlert } from 'lucide-react'
import { toast } from 'sonner'
import { api, downloadAuditCsv, useAsync } from '@/lib/api'
import { formatTime } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/Pagination'
import { PageHeader } from '@/components/PageHeader'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/TableSkeleton'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const PAGE_SIZES = [20, 50, 100] as const

type BadgeVariant = 'default' | 'success' | 'warning' | 'destructive' | 'outline'

/** 与总览一致的配色规则：设备密钥类 warning、仪器激活码类主色、状态变更 success、管理类 destructive。 */
function actionVariant(action: string): BadgeVariant {
  if (action.startsWith('secret.')) return 'warning'
  if (action.startsWith('activation.')) return 'default'
  if (action === 'device.status') return 'success'
  if (action.startsWith('dynamic_password.')) return 'outline'
  if (action.startsWith('admin.')) return 'destructive'
  return 'outline'
}

/** detail 是 JSON 字符串或纯文本：列表里截断展示，title 里给出美化后的完整内容。 */
function prettyDetail(detail: string): string {
  try {
    return JSON.stringify(JSON.parse(detail), null, 2)
  } catch {
    return detail
  }
}

/** 变更值压成一行摘要（列表空间有限，完整内容放 title）。 */
function compactValue(raw: string | null): string {
  if (raw === null) return '—'
  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const entries = Object.entries(parsed as Record<string, unknown>)
      return entries.map(([k, v]) => `${k}=${String(v)}`).join(' ')
    }
    return String(parsed)
  } catch {
    return raw
  }
}

export function AuditPage() {
  const [actionInput, setActionInput] = useState('')
  const [targetInput, setTargetInput] = useState('')
  const [applied, setApplied] = useState({ action: '', target: '' })
  const [pageSize, setPageSize] = useState<number>(20)
  const [page, setPage] = useState(1)
  const [exporting, setExporting] = useState(false)

  const { data, error, loading, reload } = useAsync(
    () => api.listAudit({ page, pageSize, action: applied.action, target: applied.target }),
    [page, pageSize, applied],
  )

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const filtersActive = applied.action !== '' || applied.target !== ''

  function applyFilters(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setApplied({ action: actionInput.trim(), target: targetInput.trim() })
    // 条件未变时也强制刷新一次，让「查询」始终有反馈
    reload()
  }

  function resetFilters() {
    setActionInput('')
    setTargetInput('')
    setApplied({ action: '', target: '' })
    setPage(1)
  }

  /**
   * 导出 CSV。
   *
   * 导出的是**当前筛选条件下的全部记录**，不是这一页 —— 这正是最容易误解的地方，
   * 所以按钮 title 与成功提示都把范围和条数写明。真正的过滤在服务端做（见
   * `/api/audit/export`），这里只负责把 Blob 落到用户的下载目录。
   */
  async function exportCsv() {
    setExporting(true)
    try {
      const { blob, filename, rows } = await downloadAuditCsv(applied)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      // 立刻回收：Blob 已经交给浏览器下载，留着只会占内存
      URL.revokeObjectURL(url)
      // 优先用服务端上报的实际行数：它比列表 total 多一条（本次导出自身的留痕）
      toast.success(`已导出 ${rows ?? total} 条审计记录`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '导出失败')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader title="操作日志" />

      <Card>
        <CardContent className="p-5">
          <form
            onSubmit={applyFilters}
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_140px_auto] lg:items-end"
          >
            <div className="space-y-1.5">
              <Label htmlFor="audit-action">动作关键词</Label>
              <Input
                id="audit-action"
                value={actionInput}
                onChange={(e) => setActionInput(e.target.value)}
                placeholder="如 secret、activation、dynamic_password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="audit-target">对象关键词</Label>
              <Input
                id="audit-target"
                value={targetInput}
                onChange={(e) => setTargetInput(e.target.value)}
                placeholder="设备编号，如 DSK-0001"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="audit-page-size">每页条数</Label>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPage(1)
                }}
              >
                <SelectTrigger id="audit-page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size} 条 / 页
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Search className="size-4" />
                )}
                查询
              </Button>
              <Button type="button" variant="outline" onClick={resetFilters} disabled={loading}>
                重置
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={exportCsv}
                disabled={loading || exporting}
                title="导出当前筛选条件下的全部记录（不受分页影响）"
              >
                {exporting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Download className="size-4" />
                )}
                导出 CSV
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {error ? (
        <div className="flex items-start gap-2 rounded-md border border-[var(--color-destructive)] p-3 text-xs text-[var(--color-destructive)]">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">加载失败：{error}</span>
          <Button variant="ghost" size="sm" onClick={reload}>
            <RefreshCw className="size-3.5" />
            重试
          </Button>
        </div>
      ) : null}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[168px]">时间</TableHead>
                <TableHead className="w-[120px]">操作者</TableHead>
                <TableHead className="w-[190px]">动作</TableHead>
                <TableHead className="w-[160px]">对象</TableHead>
                <TableHead>详情</TableHead>
                <TableHead className="w-[130px]">IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && !data ? (
                <TableSkeleton rows={6} cols={6} />
              ) : rows.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={6}>
                    <EmptyState
                      title={filtersActive ? '没有符合条件的审计记录' : '暂无审计记录'}
                      action={
                        page > 1 ? (
                          <Button variant="outline" size="sm" onClick={() => setPage(1)}>
                            回到第一页
                          </Button>
                        ) : filtersActive ? (
                          <Button variant="outline" size="sm" onClick={resetFilters}>
                            清除筛选条件
                          </Button>
                        ) : undefined
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums text-[var(--color-muted-foreground)]">
                      {formatTime(row.created_at)}
                    </TableCell>
                    <TableCell className="truncate text-sm">{row.actor ?? '系统'}</TableCell>
                    <TableCell>
                      <Badge variant={actionVariant(row.action)} className="font-mono-token">
                        {row.action}
                      </Badge>
                    </TableCell>
                    <TableCell
                      className="font-mono-token max-w-[160px] truncate text-xs"
                      title={row.target ?? ''}
                    >
                      {row.target ?? '—'}
                    </TableCell>
                    <TableCell className="max-w-[320px]">
                      {row.detail ? (
                        <span
                          className="font-mono-token block truncate text-xs text-[var(--color-muted-foreground)]"
                          title={prettyDetail(row.detail)}
                        >
                          {row.detail}
                        </span>
                      ) : null}
                      {/* docs/11 要求审计含「修改前/修改后」——旧记录没有这两列，故按需渲染 */}
                      {row.old_value !== null || row.new_value !== null ? (
                        <span
                          className="mt-1 block truncate text-[11px] text-[var(--color-muted-foreground)]"
                          title={`修改前：\n${prettyDetail(row.old_value ?? 'null')}\n\n修改后：\n${prettyDetail(row.new_value ?? 'null')}`}
                        >
                          <span className="text-[var(--color-foreground)]">变更：</span>
                          {compactValue(row.old_value)} → {compactValue(row.new_value)}
                        </span>
                      ) : null}
                      {!row.detail && row.old_value === null && row.new_value === null ? (
                        <span className="text-xs text-[var(--color-muted-foreground)]">—</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="font-mono-token truncate text-xs text-[var(--color-muted-foreground)]">
                      {row.ip ?? '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        disabled={loading}
        onChange={setPage}
      />
    </div>
  )
}
